import React from 'react';
import I18n from 'i18n-js';
import { InboxOutlined } from '@ant-design/icons';
import { Upload, Typography, Spin } from 'antd';
import {
  arrayOf, number, shape, string, oneOfType, func, bool,
} from 'prop-types';
import useErrors from 'hooks/forms/useErrors';
import { TicketAttachmentListItem } from './TicketAttachmentListItem';

const { Text } = Typography;
const { Dragger } = Upload;

const MAX_FILE_SIZE_MB = 100;

const ALLOWED_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'gif', 'png', 'bmp', 'tif', 'tiff', 'heic', 'heif',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'odt', 'ods', 'odp', 'pdf',
  'numbers', 'pages', 'key',
  'zip', 'rar', 'gz', 'tar', '7z',
  'csv', 'txt', 'json', 'xml', 'rtf', 'sig', 'geojson', 'kml', 'crp',
  'mp4', 'mov', 'avi', 'mkv', 'webm',
]);

const ACCEPT = [...ALLOWED_EXTENSIONS].map((ext) => `.${ext}`).join(',');

const Attachments = ({
  fileList = [],
  setFileList = (_files) => {},
  isCurrentUserAuthor = true,
  isLoading = false,
}) => {
  const { errors, setErrors, clearErrors } = useErrors();

  const setFileError = (file, key, message) => {
    setErrors((prev) => ({ ...prev, [file.uid]: [{ key, message }] }));
    // eslint-disable-next-line no-param-reassign
    file.status = 'error';
  };

  const validateFile = (file) => {
    const extension = file.name?.split('.').pop()?.toLowerCase();

    if (!extension || !ALLOWED_EXTENSIONS.has(extension)) {
      const formats = [...ALLOWED_EXTENSIONS].join(', ');
      setFileError(
        file,
        'invalid_file_type',
        I18n.t('tickets.form.invalid_file_type', { formats }),
      );
      return false;
    }

    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      setFileError(
        file,
        'size_too_big',
        // eslint-disable-next-line camelcase
        I18n.t('errors.messages.size_too_big', { file_size: `${MAX_FILE_SIZE_MB} MB` }),
      );
      return false;
    }

    return true;
  };

  const markStoredFileForDelete = (file) => {
    setFileList((prev) => prev.map(
      (f) => (f.uid === file.uid ? { ...f, markedForDelete: true } : f),
    ));
  };

  const itemRender = (originNode, file, _antFileList, { remove }) => {
    if (file.markedForDelete) return null;

    const handleHide = file.id ? () => markStoredFileForDelete(file) : remove;

    return (
      <TicketAttachmentListItem
        clearErrors={clearErrors}
        errors={errors[file.uid]}
        file={file}
        hideItem={handleHide}
        isCurrentUserAuthor={isCurrentUserAuthor}
      />
    );
  };

  const uploadProps = {
    accept: ACCEPT,
    onRemove: (file) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    beforeUpload: (file, files) => {
      for (const f of files) {
        validateFile(f);
      }
      setFileList((prev) => [
        ...prev,
        ...files.filter((f) => prev.every((p) => p.uid !== f.uid)),
      ]);
      return Upload.LIST_IGNORE;
    },
    fileList,
    multiple: true,
    itemRender,
  };

  return (
    <Spin spinning={isLoading}>
      <div className="documents-dropzone">
        {isCurrentUserAuthor ? (
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <Text>{I18n.t('tickets.form.attachments_hint')}</Text>
          </Dragger>
        ) : (
          <Upload {...uploadProps} />
        )}
      </div>
    </Spin>
  );
};

Attachments.propTypes = {
  fileList: arrayOf(shape({
    url: string,
    name: string,
    uid: oneOfType([number, string]),
    status: string,
    percent: number,
    response: shape({}),
    size: number,
    type: string,
    id: number,
    markedForDelete: bool,
    originFileObj: shape({}),
  })),
  setFileList: func,
  isCurrentUserAuthor: bool,
  isLoading: bool,
};

export default Attachments;
